import { Component, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TreeNode, Path, PermissionVerb, Permission, PermissionSubject, Tree, PermissionLookup } from '@s8l/client-tree-lib';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, mergeMap } from 'rxjs/operators';

import { CmsService } from 'src/app/services/cms.service';
import { NotificationsService } from 'src/app/services/notifications.service';

import { PermissionToggleConfig } from '../permission-toggles/permission-toggles.component';

@Component({
  selector: 'app-permission-table',
  templateUrl: './permission-table.component.html',
  styleUrls: ['./permission-table.component.scss']
})
export class PermissionTableComponent implements OnInit, OnDestroy {
  private static readonly FullAccess = PermissionVerb.Read | PermissionVerb.Write | PermissionVerb.Delete;

  @Input() public roles: string[] = [];

  // Do not allow remove permissions on these roles
  @Input() public protectRoles: string[] = ['api', 'admin'];

  @Input() public resources = true;

  @Input() public set subject(value: PermissionSubject) {
    this._subject = value;
    this._subjectKey = value.key();
  }

  @Input() public set updates(value: PermissionSubject[]) {
    this._updateSubjects = value;
  }

  public get updates(): PermissionSubject[] {
    return this._updateSubjects ?? [this._subject];
  }

  public root: TreeNode;
  public searchModel = '';
  public searchTerm = new Subject<string>();
  public searchResult: TreeNode[] = [];
  public expand: { [uuid: string]: boolean } = {};
  public loadingChildren: { [uuid: string]: boolean } = {};

  public loading = false;
  public protect = false;
  public progress = 0;
  public progressMax = 0;

  // Main subject to read/display
  private _subject: PermissionSubject = PermissionSubject.Node;

  // For performance
  private _subjectKey: string = PermissionSubject.Node.key();

  // Subjects to write all together, only write the main subject if null
  private _updateSubjects: PermissionSubject[] = null;

  private tree: Tree;
  private searching: Subscription;
  private subscription: Subscription;

  private prettyPathsCache: { [uri: string]: string } = {};
  private configCache: { [nodePath: string]: PermissionToggleConfig } = {};
  private hasProtectRole = false;

  constructor(private cms: CmsService, private notification: NotificationsService) {}

  public ngOnInit(): void {
    this.subscription = this.cms.tree.subscribe(tree => {
      this.tree = tree;
      this.root = tree.root;
      // Clear cache when tree changes (e.g., lazy-loaded nodes)
      this.configCache = {};
    });
    this.searching = this.searchTerm
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        mergeMap(term => of(term).pipe(delay(100)))
      )
      .subscribe(term => this.doSearch(term));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.configCache = {};
    this.hasProtectRole = this.protectRoles.some(cr => this.roles.includes(cr));
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.searching?.unsubscribe();
  }

  private doSearch(term: string): void {
    term = term.toLowerCase();
    // With lazy loading, search only through loaded nodes
    // Use a shallow search helper that doesn't require all descendants to be loaded
    this.searchResult = this.flattenLoadedNodes(this.root).filter(c => c.name.toLowerCase().includes(term));
  }

  private flattenLoadedNodes(node: TreeNode, depth: number = 0, maxDepth: number = 10): TreeNode[] {
    // Limit depth to prevent infinite recursion with lazy loading
    // Only traverse nodes that have actually been loaded
    if (depth > maxDepth || !node.children) {
      return [node];
    }
    return [node, ...node.children.flatMap(c => this.flattenLoadedNodes(c, depth + 1, maxDepth))];
  }

  public async toggleExpand(node: TreeNode): Promise<void> {
    // If expanding and children are not loaded, load them first
    if (!this.expand[node.uuid] && (!node.children || node.children.length === 0)) {
      await this.loadChildren(node);
    }
    this.expand[node.uuid] = !this.expand[node.uuid];
  }

  private async loadChildren(node: TreeNode): Promise<void> {
    if (this.loadingChildren[node.uuid]) {
      return;
    }

    try {
      this.loadingChildren[node.uuid] = true;

      // Fetch children for this specific node using its UUID
      const response = await this.cms.cms.treeGet({
        language: this.cms.language,
        parentUuid: node.uuid,
        depth: 1
      });

      // Update the node with the fetched children
      if (response.root && response.root.children) {
        const rawFetchedChildren = response.root.children || [];

        // Convert plain objects to actual TreeNode instances
        const fetchedChildren = rawFetchedChildren.map(rawChild => this.createTreeNode(rawChild));

        // Deserialize and process fetched children
        fetchedChildren.forEach(fetchedChild => {
          if (fetchedChild.permissions && typeof fetchedChild.permissions === 'object' && !('grants' in fetchedChild.permissions)) {
            fetchedChild.permissions = PermissionLookup.of(fetchedChild.permissions);
          }
          if (!fetchedChild.children) {
            fetchedChild.children = [];
          }

          // Find matching child in existing children by uuid
          const existingChild = node.children?.find(c => c.uuid === fetchedChild.uuid);
          if (existingChild) {
            // Update only the grandchildren
            existingChild.children = (fetchedChild.children || []).map(grandchild => this.createTreeNode(grandchild));
          } else {
            // Child didn't exist, add it
            if (!node.children) {
              node.children = [];
            }
            node.children.push(fetchedChild);
          }
        });

        // Invalidate cache for this node's children permissions
        node.children.forEach(child => {
          delete this.configCache[child.path];
        });
      }
    } catch (error) {
      console.error(`Failed to load children for node ${node.path}:`, error);
    } finally {
      this.loadingChildren[node.uuid] = false;
    }
  }

  private createTreeNode(data: any): TreeNode {
    // Convert plain objects to actual TreeNode instances with all methods
    return new TreeNode({
      ...data,
      children: (data.children || []).map((child: any) => this.createTreeNode(child))
    });
  }

  public trackByUUID(index: number, node: TreeNode) {
    return node.uuid;
  }

  public prettyPath(node: TreeNode) {
    let pretty = this.prettyPathsCache[node.path];
    if (!pretty) {
      pretty = Path.parse(node.path).removeFirst().get().join(' / ');
      this.prettyPathsCache[node.path] = pretty;
    }
    return pretty;
  }

  public getConfig(node: TreeNode): PermissionToggleConfig {
    const cacheKey = node.path;
    const match = this.configCache[cacheKey];
    if (match) return match;

    // Safe navigation for lazy-loaded nodes that might not have permissions data
    const grants = node.permissions?.grants ?? {};
    const verbs = this.roles.map(role => grants[role] ?? {}).map(verbs => verbs[this._subjectKey] ?? 0);
    const any = verbs.reduce((a, b) => a | b) ?? 0;
    const all = verbs.reduce((a, b) => a & b) ?? 0;

    // Whether the recursive-toggle button should be enabled: must reflect
    // real children existence, not just whether they've been lazy-loaded
    // into the browser yet - almost none are, for a large collapsed tree.
    const chl = node.hasChildren ?? node.children?.length > 0;
    // Whether chlAll/chlAny below are trustworthy: only true once children
    // are actually loaded. A collapsed node can have chl === true (it does
    // have children) while this is false (we haven't looked at them yet) -
    // the "some sub-nodes don't match" warning must stay hidden in that case
    // rather than compare against a null/unknown value.
    const chlKnown = node.children?.length > 0;
    let chlAny: number = null;
    let chlAll: number = null;
    if (chlKnown) {
      try {
        const chlCnf = node.children?.map(c => this.getConfig(c));
        chlAny = chlCnf.map(c => c.any | (c.chlAny ?? 0)).reduce((a, b) => a | b);
        chlAll = chlCnf.map(c => c.all & (c.chlAll ?? PermissionTableComponent.FullAccess)).reduce((a, b) => a & b);
      } catch (e) {
        // If child config calculation fails (e.g., due to missing permissions), skip it
        console.warn(`Failed to calculate child permissions for ${node.path}:`, e);
      }
    }
    const config = { all, any, rec: true, chl, chlAll, chlAny, chlKnown, protect: this.hasProtectRole };
    this.configCache[cacheKey] = config;
    return config;
  }

  public async permissionChanged(node: TreeNode, event: { verb: PermissionVerb; state: boolean; recursive: boolean }) {
    this.loading = true;
    try {
      if (event.recursive) {
        // Applied server-side against the whole subtree (not just whatever
        // happens to be loaded client-side) - see
        // CmsService.updatePermissionRecursive.
        await this.setupPermissionsRecursive(node, event.verb, event.state);
      } else {
        await this.setupPermissions([node], event.verb, event.state);
        // Invalidate cache for changed nodes and all parent paths
        // Use paths instead of node objects to avoid loading unloaded parent nodes
        this.invalidateNodeAndParentCaches(node);
        delete this.configCache[node.path];
      }
    } finally {
      this.loading = false;
    }
  }

  private invalidateNodeAndParentCaches(node: TreeNode): void {
    // Invalidate cache for this node
    delete this.configCache[node.path];

    // Invalidate cache for all parent paths without needing to load them
    const parentPaths = Path.parse(node.path).explode().slice(0, -1);

    parentPaths.forEach(path => {
      delete this.configCache[path.toString()];
    });
  }

  private async setupPermissionsRecursive(node: TreeNode, verb: PermissionVerb, add: boolean) {
    const protectedRole = this.protectRoles.find(cr => this.roles.includes(cr));
    if (protectedRole && !add) {
      this.notification.notify('warning', 'Cannot revoke permission', 'Role "' + protectedRole + '" is protected!');
      return;
    }
    this.progress = 0;
    this.progressMax = this.roles.length;
    const perms = this.updates.map(subject => new Permission(subject, verb));

    let changed = 0;
    let total = 0;
    for (const role of this.roles) {
      const response = await this.cms.updatePermissionRecursive(role, add, node, perms);
      changed += response.changed;
      total += response.total;
      this.progress++;
    }
    // A recursive update can touch an arbitrary number of nodes across the
    // subtree - unlike the single-node toggle, there's no cheap way to know
    // exactly which cached paths are now stale, so drop the whole cache
    // rather than risk leaving some checkbox (the node itself included)
    // showing pre-update state until a full reload.
    this.configCache = {};
    this.notification.notify(
      'success',
      add ? 'Permission granted' : 'Permission revoked',
      `Updated ${changed} of ${total} node/role combinations in "${this.prettyPath(node) || node.name}" and its subtree.`
    );
  }

  private async setupPermissions(nodes: TreeNode[], verb: PermissionVerb, add: boolean, forceUpdate = false) {
    const protectedRole = this.protectRoles.find(cr => this.roles.includes(cr));
    if (protectedRole && !add) {
      this.notification.notify('warning', 'Cannot revoke permission', 'Role "' + protectedRole + '" is protected!');
      return;
    }
    this.progress = 0;
    this.progressMax = nodes.length * this.roles.length;
    const perms = this.updates.map(subject => new Permission(subject, verb));
    this.progress = 1;
    for (const node of nodes) {
      for (const role of this.roles) {
        // Do a quick local check whether the base permission already matches the desired value
        if (forceUpdate || ((node.permissions.getAccess([role], this._subject) & verb) == 0) == add) {
          await this.cms.updateAccessVerb(role, add, node, perms);
        }
        this.progress++;
      }
    }
  }
}
